import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { founderAPI } from '../lib/api';
import { motion } from 'framer-motion';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Shield, Target, TrendingUp, Users, Zap, DollarSign, Activity,
  Gauge, Crown, LogOut, Cpu, ArrowUpRight, Radio,
} from 'lucide-react';

const ORANGE = '#FF7A3D';
const TEAL = '#00E5C1';

function useAnimatedNumber(value, duration = 1200) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = Number(value) || 0;
    if (start === end) { setDisplay(end); return; }
    const t0 = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(start + (end - start) * eased);
      if (p < 1) raf = requestAnimationFrame(step);
      else prev.current = end;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return display;
}

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMoneyShort = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

function Countdown({ targetDate }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const target = new Date(targetDate).getTime();
  const diff = Math.max(target - now, 0);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const unit = (val, label) => (
    <div className="flex flex-col items-center" data-testid={`countdown-${label.toLowerCase()}`}>
      <span className="font-heading text-2xl md:text-3xl text-white tabular-nums leading-none">
        {String(val).padStart(2, '0')}
      </span>
      <span className="font-mono text-[9px] text-[#7a8599] tracking-[0.2em] mt-1.5">{label}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-4 md:gap-5" data-testid="pat-countdown-timer">
      {unit(days, 'DAYS')}
      <span className="text-[#2a3142] text-xl -mt-3">:</span>
      {unit(hours, 'HRS')}
      <span className="text-[#2a3142] text-xl -mt-3">:</span>
      {unit(mins, 'MIN')}
      <span className="text-[#2a3142] text-xl -mt-3">:</span>
      {unit(secs, 'SEC')}
    </div>
  );
}

function PatHud({ pat }) {
  const animatedPat = useAnimatedNumber(pat.current_pat, 1600);
  const progress = pat.progress_pct || 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden rounded-lg border border-[#FF7A3D]/25 bg-[#07090d]/80 backdrop-blur-xl p-6 md:p-9"
      style={{ boxShadow: '0 0 60px rgba(255,122,61,0.07), inset 0 1px 0 rgba(255,255,255,0.03)' }}
      data-testid="pat-countdown-hud"
    >
      <div className="absolute inset-0 opacity-[0.5] pointer-events-none" style={{ background: 'radial-gradient(circle at 15% 20%, rgba(255,122,61,0.1), transparent 45%), radial-gradient(circle at 85% 80%, rgba(0,229,193,0.08), transparent 45%)' }} />
      <div className="relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-7">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <Target className="w-4 h-4 text-[#FF7A3D]" />
              <span className="font-mono text-[11px] text-[#FF7A3D] tracking-[0.3em] uppercase">Mission · $10M PAT Milestone</span>
            </div>
            <p className="font-heading text-4xl md:text-5xl lg:text-6xl text-white tracking-tight tabular-nums leading-none">
              {fmtMoney(animatedPat)}
            </p>
            <p className="font-mono text-xs text-[#7a8599] mt-3 tracking-wide">
              PROFIT AFTER TAX · TARGET <span className="text-[#00E5C1]">{fmtMoneyShort(pat.target)}</span> · MARGIN {(pat.margin * 100).toFixed(0)}%
            </p>
          </div>
          <div className="lg:text-right">
            <p className="font-mono text-[10px] text-[#7a8599] tracking-[0.25em] mb-3 lg:text-right">COUNTDOWN TO TARGET</p>
            <Countdown targetDate={pat.target_date} />
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] text-[#7a8599] tracking-widest">PROGRESS</span>
            <span className="font-mono text-[11px] text-[#00E5C1] tracking-wider tabular-nums" data-testid="pat-progress-value">
              {progress.toFixed(progress < 1 ? 4 : 2)}%
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-white/[0.04] border border-white/[0.05] overflow-hidden" data-testid="pat-progress-bar">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.max(progress, 0.4)}%` }}
              transition={{ duration: 1.4, ease: 'easeOut' }}
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${ORANGE}, ${TEAL})`, boxShadow: `0 0 12px ${ORANGE}80` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/[0.05]">
          <div data-testid="pat-velocity">
            <p className="font-mono text-[9px] text-[#7a8599] tracking-[0.2em] mb-1.5">MoM GROWTH</p>
            <p className="font-heading text-lg text-[#00E5C1]">{pat.months_remaining ? '' : ''}{(pat.required_mom_growth_pct ?? 0)}%<span className="text-[#7a8599] text-xs ml-1 font-mono">req.</span></p>
          </div>
          <div>
            <p className="font-mono text-[9px] text-[#7a8599] tracking-[0.2em] mb-1.5">REQ. MONTHLY REV</p>
            <p className="font-heading text-lg text-white">{fmtMoneyShort(pat.required_monthly_revenue)}</p>
          </div>
          <div>
            <p className="font-mono text-[9px] text-[#7a8599] tracking-[0.2em] mb-1.5">MONTHS LEFT</p>
            <p className="font-heading text-lg text-[#FF7A3D]">{pat.months_remaining}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color, testid, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="relative rounded-lg border border-white/[0.06] bg-[#0a0c11]/70 backdrop-blur-md p-5 overflow-hidden group hover:border-[#00E5C1]/30 transition-colors duration-300"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${color}12`, border: `1px solid ${color}25` }}>
          <Icon className="w-[18px] h-[18px]" style={{ color }} />
        </div>
        {sub && <span className="font-mono text-[10px] tracking-wider flex items-center gap-1" style={{ color }}><ArrowUpRight className="w-3 h-3" />{sub}</span>}
      </div>
      <p className="font-heading text-2xl md:text-[1.8rem] text-white leading-none tabular-nums">{value}</p>
      <p className="font-mono text-[10px] text-[#7a8599] tracking-[0.2em] mt-2">{label}</p>
    </motion.div>
  );
}

function ChartTooltip({ active, payload, label, prefix = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0a0c11] border border-white/10 rounded-sm p-2.5 font-mono text-[11px]">
      <p className="text-[#7a8599] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{prefix}{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</p>
      ))}
    </div>
  );
}

function ActivityStream() {
  const [feed, setFeed] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await founderAPI.activity(18);
        if (alive) setFeed(res.data.activity || []);
      } catch {}
    };
    load();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#0a0c11]/70 backdrop-blur-md p-5 h-full" data-testid="live-agent-activity-stream">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <Radio className="w-4 h-4 text-[#00E5C1]" />
          <h2 className="font-heading text-sm text-white tracking-wide">LIVE AGENT ACTIVITY</h2>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00E5C1] animate-pulse" />
          <span className="font-mono text-[9px] text-[#00E5C1] tracking-[0.2em]">STREAMING</span>
        </div>
      </div>
      <div className="space-y-1.5 max-h-[340px] overflow-y-auto pr-1">
        {feed.length === 0 ? (
          <div className="py-10 text-center">
            <p className="font-mono text-xs text-[#7a8599]">Awaiting agent executions…</p>
            <p className="font-mono text-[10px] text-[#4a5567] mt-1">Live runs will stream here in real time</p>
          </div>
        ) : feed.map((a, i) => (
          <motion.div
            key={a.exec_id || i}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: Math.min(i * 0.02, 0.3) }}
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm bg-white/[0.015] border border-white/[0.04] hover:border-[#FF7A3D]/20 transition-colors"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5C1] flex-shrink-0" />
              <span className="font-mono text-[11px] text-white truncate">{a.agent_name}</span>
              <span className="font-mono text-[10px] text-[#4a5567] hidden sm:inline">{a.operative}</span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="font-mono text-[9px] text-[#7a8599] uppercase hidden md:inline">{a.provider}</span>
              <span className="font-mono text-[10px] text-[#FF7A3D] tabular-nums">{a.credits_used.toFixed(2)}cr</span>
              <span className="font-mono text-[8px] text-[#00E5C1] bg-[#00E5C1]/10 px-1.5 py-0.5 rounded-sm">{a.status}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function FounderCockpit() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await founderAPI.cockpit();
        if (alive) setData(res.data);
      } catch {}
    };
    load();
    const poll = setInterval(load, 30000);
    const tick = setInterval(() => setClock(new Date()), 1000);
    return () => { alive = false; clearInterval(poll); clearInterval(tick); };
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#020306] flex items-center justify-center" data-testid="cockpit-loading">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#FF7A3D] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-mono text-[#FF7A3D] text-xs tracking-[0.3em]">INITIALIZING COMMAND CENTER…</p>
        </div>
      </div>
    );
  }

  const { kpis, revenue, pat, charts, top_agents, llm_providers, uptime_pct } = data;

  return (
    <div className="min-h-screen bg-[#020306] scanlines" data-testid="founder-cockpit">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-[#020306]/90 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-[#FF7A3D]" />
            <div>
              <p className="font-heading text-sm text-white tracking-wide leading-none">FOUNDER COMMAND CENTER</p>
              <p className="font-mono text-[9px] text-[#7a8599] tracking-[0.25em] mt-1">DEFENCES.AI · WAR ROOM</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="hidden md:flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5C1] animate-pulse" />
              <span className="font-mono text-[10px] text-[#7a8599] tabular-nums tracking-wider">
                {clock.toUTCString().slice(17, 25)} UTC
              </span>
            </div>
            <Link to="/dashboard" className="font-mono text-[10px] text-[#7a8599] hover:text-[#FF7A3D] transition-colors tracking-wider" data-testid="cockpit-to-dashboard">DASHBOARD</Link>
            <button onClick={() => { logout(); navigate('/'); }} className="flex items-center gap-1.5 font-mono text-[10px] text-[#7a8599] hover:text-[#ff4d4d] transition-colors tracking-wider" data-testid="cockpit-exit">
              <LogOut className="w-3.5 h-3.5" /> EXIT
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-7 space-y-6">
        <PatHud pat={pat} />

        {/* Primary KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={DollarSign} label="MONTHLY RECURRING REV" value={fmtMoneyShort(revenue.mrr)} sub={`${revenue.mom_growth_pct >= 0 ? '+' : ''}${revenue.mom_growth_pct}%`} color={TEAL} testid="mrr-kpi-card" delay={0.05} />
          <KpiCard icon={TrendingUp} label="PROJECTED ARR" value={fmtMoneyShort(revenue.arr)} sub="run-rate" color={ORANGE} testid="arr-kpi-card" delay={0.1} />
          <KpiCard icon={Zap} label="EXECUTIONS TODAY" value={kpis.executions_today.toLocaleString()} sub="live" color="#ffbd2e" testid="executions-today-kpi" delay={0.15} />
          <KpiCard icon={Users} label="PAYING SUBSCRIBERS" value={kpis.active_subscribers.toLocaleString()} sub={`${kpis.total_users} total`} color="#c084fc" testid="subscribers-kpi" delay={0.2} />
        </div>

        {/* Secondary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <KpiCard icon={DollarSign} label="TOTAL REVENUE" value={fmtMoneyShort(revenue.total_revenue)} color={TEAL} testid="total-revenue-kpi" delay={0.05} />
          <KpiCard icon={Activity} label="TOTAL EXECUTIONS" value={kpis.executions_total.toLocaleString()} color={ORANGE} testid="total-exec-kpi" delay={0.08} />
          <KpiCard icon={Shield} label="ACTIVE AGENTS" value={`${kpis.active_agents}/${kpis.total_agents}`} color="#ff4d6d" testid="active-agents-kpi" delay={0.11} />
          <KpiCard icon={DollarSign} label="REVENUE TODAY" value={fmtMoneyShort(revenue.today_revenue)} color="#ffbd2e" testid="revenue-today-kpi" delay={0.14} />
          <KpiCard icon={Gauge} label="UPTIME" value={`${uptime_pct}%`} color={TEAL} testid="uptime-kpi" delay={0.17} />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-lg border border-white/[0.06] bg-[#0a0c11]/70 backdrop-blur-md p-5" data-testid="revenue-trend-chart">
            <h2 className="font-heading text-sm text-white mb-4 tracking-wide">REVENUE TREND · 30D</h2>
            {charts.daily_revenue?.length > 0 ? (
              <ResponsiveContainer width="100%" height={210}>
                <AreaChart data={charts.daily_revenue}>
                  <defs>
                    <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={TEAL} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: '#4a5567', fontSize: 9, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#1a1f2a' }} tickLine={false} />
                  <YAxis tick={{ fill: '#4a5567', fontSize: 9, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#1a1f2a' }} tickLine={false} />
                  <Tooltip content={<ChartTooltip prefix="$" />} />
                  <Area type="monotone" dataKey="amount" stroke={TEAL} fill="url(#revFill)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="h-[210px] flex items-center justify-center"><p className="font-mono text-xs text-[#4a5567]">No revenue yet — projections active</p></div>}
          </div>
          <div className="rounded-lg border border-white/[0.06] bg-[#0a0c11]/70 backdrop-blur-md p-5" data-testid="executions-trend-chart">
            <h2 className="font-heading text-sm text-white mb-4 tracking-wide">AGENT EXECUTIONS · 30D</h2>
            {charts.daily_executions?.length > 0 ? (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={charts.daily_executions}>
                  <XAxis dataKey="date" tick={{ fill: '#4a5567', fontSize: 9, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#1a1f2a' }} tickLine={false} />
                  <YAxis tick={{ fill: '#4a5567', fontSize: 9, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#1a1f2a' }} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="count" fill={ORANGE} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-[210px] flex items-center justify-center"><p className="font-mono text-xs text-[#4a5567]">No executions yet</p></div>}
          </div>
        </div>

        {/* Activity + Leaderboard */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2"><ActivityStream /></div>
          <div className="rounded-lg border border-white/[0.06] bg-[#0a0c11]/70 backdrop-blur-md p-5" data-testid="top-agents-leaderboard">
            <h2 className="font-heading text-sm text-white mb-4 tracking-wide">TOP AGENTS</h2>
            {top_agents?.length > 0 ? (
              <div className="space-y-2">
                {top_agents.map((a, i) => (
                  <div key={a.agent_id} className="flex items-center justify-between px-3 py-2 rounded-sm bg-white/[0.015] border border-white/[0.04]">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="font-mono text-[10px] text-[#FF7A3D] w-5 flex-shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <span className="font-mono text-[11px] text-white truncate">{a.name}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="font-mono text-[10px] text-[#7a8599]">{a.runs} runs</span>
                      <span className="font-mono text-[10px] text-[#00E5C1] tabular-nums">{a.revenue.toFixed(1)}cr</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="font-mono text-xs text-[#4a5567] py-6 text-center">No agent runs yet</p>}

            {/* LLM providers health */}
            <div className="mt-5 pt-5 border-t border-white/[0.05]">
              <div className="flex items-center gap-2 mb-3">
                <Cpu className="w-3.5 h-3.5 text-[#7a8599]" />
                <h3 className="font-mono text-[10px] text-[#7a8599] tracking-[0.2em]">LLM ENGINE STATUS</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {llm_providers.map(p => (
                  <span key={p.name} className="flex items-center gap-1.5 font-mono text-[9px] text-[#cbd5e1] bg-white/[0.02] border border-white/[0.05] px-2 py-1 rounded-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00E5C1]" />{p.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <p className="font-mono text-[9px] text-[#3a4150] text-center tracking-wider pt-2">
          PRIVATE · FOUNDER EYES ONLY · DATA REFRESHES EVERY 30s · ACTIVITY STREAM EVERY 5s
        </p>
      </div>
    </div>
  );
}
